import { useState } from "react";
import { trpc } from "@/lib/trpc";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@tanstack/react-router";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  "COMMUNITY_ADMIN",
  "RESIDENT",
  "SECURITY",
  "MAINTENANCE",
  "AMENITY_STAFF",
] as const;

type Role = (typeof ROLES)[number];

const ROLE_LABELS: Record<Role, string> = {
  COMMUNITY_ADMIN: "Administrador del barrio",
  RESIDENT: "Residente",
  SECURITY: "Seguridad",
  MAINTENANCE: "Mantenimiento",
  AMENITY_STAFF: "Personal de amenities",
};

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: "Activo",
  INACTIVE: "Inactivo",
  PENDING: "Pendiente",
};

export function AdminMyAccessPage() {
  const utils = trpc.useUtils();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedCommunityId, setSelectedCommunityId] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<Role>("RESIDENT");

  const { data: memberships, isLoading: membershipsLoading } =
    trpc.admin.getMyMemberships.useQuery();
  const { data: allCommunities, isLoading: communitiesLoading } =
    trpc.admin.getAllCommunities.useQuery();

  const updateRole = trpc.admin.updateMyRole.useMutation({
    onSuccess: () => {
      utils.admin.getMyMemberships.invalidate();
      utils.amenity.getUserCommunities.invalidate();
      toast.success("Rol actualizado");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const addToCommunity = trpc.admin.addMyselfToCommunity.useMutation({
    onSuccess: () => {
      utils.admin.getMyMemberships.invalidate();
      utils.amenity.getUserCommunities.invalidate();
      setIsAddDialogOpen(false);
      setSelectedCommunityId("");
      setSelectedRole("RESIDENT");
      toast.success("Te agregaste al barrio");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const removeFromCommunity = trpc.admin.removeMyselfFromCommunity.useMutation({
    onSuccess: () => {
      utils.admin.getMyMemberships.invalidate();
      utils.amenity.getUserCommunities.invalidate();
      toast.success("Te quitaste del barrio");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleRoleChange = (communityId: string, role: Role) => {
    updateRole.mutate({ communityId, role });
  };

  const handleAddToCommunity = () => {
    if (!selectedCommunityId) return;
    addToCommunity.mutate({
      communityId: selectedCommunityId,
      role: selectedRole,
    });
  };

  const isLoading = membershipsLoading || communitiesLoading;

  // Communities the user is NOT a member of
  const availableCommunities = allCommunities?.filter(
    (c) => !memberships?.some((m) => m.communityId === c.id),
  );

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-6xl px-4 py-6 md:px-6 md:py-10">
      <div className="mb-8">
        <h1 className="text-2xl font-bold md:text-3xl">Panel de Admin</h1>
        <p className="text-muted-foreground">Gestioná usuarios y barrios.</p>
      </div>

      <div className="mb-6 flex gap-2">
        <Link
          to="/admin/users"
          className="rounded-xl px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          Usuarios
        </Link>
        <Link
          to="/admin/communities"
          className="rounded-xl px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          Barrios
        </Link>
        <Link
          to="/admin/communications"
          className="rounded-xl px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          Comunicados
        </Link>
        <Link
          to="/admin/my-access"
          className="rounded-xl px-4 py-2 text-sm font-medium bg-primary text-primary-foreground"
        >
          Mi acceso
        </Link>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Mi acceso a barrios</CardTitle>
              <CardDescription>
                Gestioná tus membresías y roles para probar distintos niveles de
                permisos.
              </CardDescription>
            </div>
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button disabled={!availableCommunities?.length}>
                  <Plus className="mr-2 h-4 w-4" />
                  Sumarme a un barrio
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Sumarme a un barrio</DialogTitle>
                  <DialogDescription>
                    Agregate a un barrio con un rol específico.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Barrio</label>
                    <Select
                      value={selectedCommunityId}
                      onValueChange={setSelectedCommunityId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar un barrio" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableCommunities?.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Rol</label>
                    <Select
                      value={selectedRole}
                      onValueChange={(v) => setSelectedRole(v as Role)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLES.map((role) => (
                          <SelectItem key={role} value={role}>
                            {ROLE_LABELS[role]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    onClick={handleAddToCommunity}
                    disabled={!selectedCommunityId || addToCommunity.isPending}
                  >
                    {addToCommunity.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Agregando...
                      </>
                    ) : (
                      "Sumarme a un barrio"
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {memberships?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No sos miembro de ningún barrio.</p>
              <p className="text-sm mt-1">
                Usá el botón de arriba para agregarte a un barrio de prueba.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {memberships?.map((membership) => (
                <div
                  key={membership.id}
                  className="flex items-center justify-between rounded-xl border p-4"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{membership.communityName}</p>
                    <Badge variant="outline" className="mt-1">
                      {STATUS_LABELS[membership.status] || membership.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="min-w-[180px]">
                      <Select
                        value={membership.role}
                        onValueChange={(value) =>
                          handleRoleChange(
                            membership.communityId,
                            value as Role,
                          )
                        }
                        disabled={
                          updateRole.isPending &&
                          updateRole.variables?.communityId ===
                            membership.communityId
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map((role) => (
                            <SelectItem key={role} value={role}>
                              {ROLE_LABELS[role]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Salir del barrio"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Salir del barrio</AlertDialogTitle>
                          <AlertDialogDescription>
                            ¿Seguro que querés salir de{" "}
                            {membership.communityName}? Podés volver a agregarte
                            después.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() =>
                              removeFromCommunity.mutate({
                                communityId: membership.communityId,
                              })
                            }
                          >
                            {removeFromCommunity.isPending &&
                            removeFromCommunity.variables?.communityId ===
                              membership.communityId ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              "Salir"
                            )}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
